import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Check, Loader2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LIGHTS =
  "https://images.pexels.com/photos/5389617/pexels-photo-5389617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Subscribe = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setMessage("");
    try {
      const res = await axios.post(`${API_BASE}/subscribe`, { email, name, source: "landing" });
      setStatus("success");
      setMessage(res.data.message || "You're in.");
      setEmail("");
      setName("");
      setTimeout(() => setStatus("idle"), 4500);
    } catch (err) {
      setStatus("error");
      setMessage(err.response?.data?.detail || "Sign up failed. Please try again.");
    }
  };

  return (
    <section
      id="subscribe"
      data-testid="subscribe-section"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0">
        <img src={LIGHTS} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center grain-overlay">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#DC2626]" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              The Fan List
            </span>
            <span className="h-px w-10 bg-[#DC2626]" />
          </div>

          <h2 className="font-anton uppercase text-white text-5xl md:text-7xl leading-[0.9] mb-5">
            Join the <span className="text-[#B91C1C]">Mob</span>
          </h2>
          <p className="text-white/70 font-manrope text-lg max-w-xl mx-auto mb-10">
            Get first dibs on tour dates, ticket pre-sales, new music drops, and merch launches.
            No spam. Just rock 'n' roll.
          </p>

          <form
            data-testid="email-form"
            onSubmit={submit}
            className="flex flex-col gap-3 max-w-xl mx-auto"
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              aria-label="Your name (optional)"
              data-testid="name-input"
              className="w-full bg-black/70 border-2 border-white/20 focus:border-[#B91C1C] outline-none text-white placeholder-white/40 px-4 py-3.5 font-manrope transition-colors"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@rocks.com"
                  aria-label="Email address"
                  data-testid="email-input"
                  className="w-full bg-black/70 border-2 border-white/20 focus:border-[#B91C1C] outline-none text-white placeholder-white/40 pl-11 pr-4 py-4 font-manrope transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="email-submit-btn"
                className="font-oswald uppercase tracking-[0.2em] text-sm bg-[#B91C1C] hover:bg-[#991B1B] disabled:opacity-60 text-white px-8 py-4 brutal-shadow whitespace-nowrap"
              >
                {status === "loading" ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Joining...
                  </span>
                ) : status === "success" ? (
                  <span className="inline-flex items-center gap-2">
                    <Check size={16} /> You're In
                  </span>
                ) : (
                  "Sign Me Up"
                )}
              </button>
            </div>
          </form>

          {message && status !== "loading" && (
            <p
              data-testid="subscribe-message"
              className={`mt-5 font-manrope text-sm ${
                status === "success" ? "text-green-400" : "text-red-400"
              }`}
            >
              {message}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};
